// ProjectsSection.jsx
import React, { useState } from "react";
import {
    Container,
    Typography,
    Stack,
    Box,
    Paper,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    Button
} from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import { ArrowBack, ArrowForward, ReadMore } from "@mui/icons-material";


// ======================= PROJECT DATA =======================

export const projects = [
    {
        title: "Conversational AI & Voice Assistant",
        description: `Developed a custom NLP & LLM-based conversational AI for a healthcare SaaS platform with voice and text support.`,
        sections: {
            "System Architecture": [
                "Built on a FastAPI backend with an interactive frontend",
                "Supports text and voice interactions, enabling natural communication",
                "Integrates custom NLP models for domain-specific understanding",
                "Uses LLMs to interpret complex queries and generate responses"
            ],
            "Functional Highlights": [
                "Designed and trained a custom NLP model for domain-specific understanding",
                "Integrated LLM capabilities for natural, conversational interactions",
                "Developed voice assistant functionality for hands-free engagement",
                "Streamlined healthcare workflows to reduce administrative workload"
            ],
            "Technical Highlights": [
                "End-to-end conversational AI pipeline (NLP + LLM + voice interface)",
                "Scalable backend architecture for production deployment",
                "Interactive frontend supporting multiple user interaction modes",
                "Optimized for real-time, accurate AI-assisted healthcare communication"
            ],
            Impact: "This project demonstrates the ability to design and deploy production-ready, interactive AI systems that combine NLP, LLM, and voice technologies to improve healthcare workflows, patient experience, and clinical efficiency."
        },
        image: require("../Images/conversational_ai.webp")
    },

    {
        title: "Intelligent Event Management & Support System Chatbot",
        description: `Developed a custom AI-driven platform with RAG architecture to streamline event scheduling, notifications, and attendee management.`,
        sections: {
            "System Architecture": [
                'Leveraged NLP and LLM models to automate event communications and intelligent query handling.',
                "Enhanced user engagement through context-aware recommendations, reminders, and interactive support powered by retrieval-augmented responses."
            ],
            "Functional Highlights": [
                "Designed and trained a custom NLP model for domain-specific understanding",
                "Integrated LLM capabilities for natural, conversational interactions",
                "Developed voice assistant functionality for hands-free engagement",
                "Streamlined healthcare workflows to reduce administrative workload"
            ],
            "Technical Highlights": [
                "End-to-end conversational AI pipeline (NLP + LLM + voice interface)",
                "Scalable backend architecture for production deployment",
                "Interactive frontend supporting multiple user interaction modes",
                "Optimized for real-time, accurate AI-assisted healthcare communication"
            ],
            Impact: "This project demonstrates the ability to design and deploy production-ready, interactive AI systems that combine NLP, LLM, and voice technologies to improve healthcare workflows, patient experience, and clinical efficiency."
        },
        image: require("../Images/conversational_ai.webp")
    },
    {
        title: "Explainable Hybrid AI (ExHAI) Framework for Early Tuberculosis Detection",
        description: `Designed and developed an Explainable Hybrid AI (ExHAI) framework for early-stage Tuberculosis diagnosis by integrating Ontology-based knowledge representation with Deep Learning.`,
        sections: {
            "System Architecture": [
                "Ontology-Based Symbolic Layer: structured medical knowledge & rule-based reasoning",
                "Deep Learning Sub-Symbolic Layer: CNNs analyze chest X-ray images",
                "Hybrid Integration: combines structured knowledge with data-driven image analysis",
                "Explainability: Grad-CAM visual explanations for predictions"
            ],
            Impact: "Enabled early and accurate TB diagnosis, improved diagnostic performance, enhanced transparency, validated with real dataset, strengthened collaboration with healthcare professionals."
        },
        image: require("../Images/explainable_hybrid_ai.webp")
    },
    {
        title: "Deep Neural Classification Using Chest X-Rays",
        description: `Developed a CNN-based deep learning model to classify chest X-ray images, differentiating diseased and healthy patients for efficient medical screening.`,
        sections: {
            "Deep Learning Architecture": [
                "Implemented CNN-based automated feature extraction",
                "Applied data augmentation and preprocessing",
                "Hyperparameter tuning and regularization",
                "Trained on diverse, labeled dataset"
            ],
            "Explainability & Validation": [
                "Used Grad-CAM to visualize regions influencing predictions",
                "Enabled clinicians to interpret AI-generated results",
                "Evaluated model performance (accuracy, precision, recall, F1-score)"
            ],
            "Technical Highlights": [
                "End-to-end CNN classification pipeline",
                "Preprocessing and feature extraction for robustness",
                "Explainable AI for clinician trust",
                "Optimized for deployment in research and clinical environments"
            ],
            Impact: "Demonstrates highly accurate, explainable, and production-ready deep learning systems for medical imaging."
        },
        image: require("../Images/Deep_neural.jpg")
    },
    {
        title: "Ontology-Driven Decision Support System",
        description: `Developed a DSS to assist clinicians by integrating structured medical knowledge, reasoning, and logical queries.`,
        sections: {
            "Symbolic Knowledge Layer": [
                "Comprehensive medical ontologies capturing tests, indicators, rules",
                "Structured reasoning for transparent clinical recommendations",
                "Logical queries for automated insights"
            ],
            "Decision Support Functionality": [
                "Automated recommendations and alerts",
                "Assists in identifying anomalies and health risks",
                "Enhanced diagnostic accuracy and consistency"
            ],
            "Technical Highlights": [
                "Ontology-based knowledge representation",
                "Logical query integration",
                "Scalable knowledge base for diverse scenarios",
                "Designed for explainability and clinical adoption"
            ],
            Impact: "Builds symbolic AI systems combining domain knowledge and reasoning to support real-world clinical decision-making."
        },
        image: require("../Images/onto_decision.jpg")
    },
    {
        title: "Optical Pulse Oximeter Using Arduino",
        description: `Designed a PPG system using SFH 7050 sensor and Arduino to measure SpO₂ in real time.`,
        sections: {
            "System Architecture": [
                "PPG Sensor Layer: measures blood flow intensity variations",
                "Signal Conversion: optical to electrical signals",
                "Signal Processing: filters and amplifies signals",
                "Embedded Analysis: Arduino analyzes data in real-time"
            ],
            "Technical Highlights": [
                "End-to-end PPG signal acquisition",
                "Hardware-software integration",
                "Real-time measurement and visualization",
                "Compact, low-cost design"
            ],
            Impact: "Enables accurate, real-time physiological monitoring suitable for clinical or research applications."
        },
        image: require("../Images/pulse-oximeter.jpg")
    },
    {
        title: "SAM (Smart Agriculture Monitoring) System",
        description: `IoT-based Smart Agriculture Monitoring system using Arduino for farm security and environmental monitoring.`,
        sections: {
            "System Architecture": [
                "Motion Detection: monitor farm perimeters",
                "IoT Connectivity: real-time alerts",
                "Optical Monitoring: enhanced observation",
                "Calamity Check: environmental hazard alerts"
            ],
            "Technical Highlights": [
                "Arduino-based multi-sensor integration",
                "Real-time alerts and notifications",
                "Scalable, low-power, easy deployment"
            ],
            Impact: "Enhances farm security, monitors environmental conditions, and improves farm management efficiency."
        },
        image: require("../Images/Default_SAM_Smart_Agriculture_Monitoring_system_1.jpg")
    }
];





// ======================= MAIN SECTION =======================

const ProjectsSection = () => {

    const [viewMode, setViewMode] = useState("compact");

    const handleViewChange = (event, newView) => {
        if (newView !== null) setViewMode(newView);
    };

    // ======================= PROJECT CARD =======================

    const ProjectCard = ({ project, reverse, viewMode }) => {

        // -------- COMPACT MODE --------
        if (viewMode === "compact") {
            return (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 4,
                        borderRadius: 3,
                        background: "#173c5c",
                        transition: "0.3s ease",
                        "&:hover": {
                            background: "#1f4c73"
                        }
                    }}
                >
                    <Grid container spacing={3} alignItems="stretch">
                        {/* Image */}
                        <Grid item xs={12} md={3}>
                            <Box
                                component="img"
                                src={project.image}
                                alt={project.title}
                                sx={{
                                    width: "100%",
                                    height: 160,
                                    objectFit: "cover",
                                    borderRadius: 2
                                }}
                            />
                        </Grid>

                        {/* Content */}
                        <Grid item xs={12} md={9} sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ color: "white", mb: 1 }}
                            >
                                {project.title}
                            </Typography>

                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: "#cfd8dc",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    mb: 2
                                }}
                            >
                                {project.description}
                            </Typography>

                            {/* Spacer pushes button to bottom */}
                            <Box sx={{ flexGrow: 1 }} />

                            {/* Read More Button */}
                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    endIcon={<ArrowForward />}
                                    onClick={() => setViewMode("detailed")}
                                    sx={{
                                        // backgroundColor: "#4db6ff",
                                        color: "white",
                                        fontWeight: 500,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        "&:hover": {
                                            backgroundColor: "#3aa6f5"
                                        }
                                    }}
                                >
                                    {viewMode === "compact" ? 'Read More' : 'View Less'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

            );
        }

        // -------- DETAILED MODE --------
        return (
            <Paper
                elevation={0}
                sx={{
                    mb: 8,
                    borderRadius: 4,
                    background: "linear-gradient(145deg, #1f4060, #173c5c)",
                    p: { xs: 3, md: 5 }
                }}
            >
                <Stack spacing={4}>

                    {/* Title */}
                    <Typography variant='h4' align='left' sx={{ mb: 2, fontWeight: 900, color: "white", fontFamily: `'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif`, }}>
                        {project.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                        variant="body1"
                        sx={{ color: "#d6e4f0", lineHeight: 1.7 }}
                    >
                        {project.description}
                    </Typography>

                    {/* Image integrated */}
                    <Box
                        component="img"
                        src={project.image}
                        alt={project.title}
                        sx={{
                            width: 300,
                            maxHeight: 300,
                            objectFit: "contain",
                            borderRadius: '20px',
                            boxShadow: 0
                        }}
                    />

                    {/* Sections */}
                    {Object.entries(project.sections).map(([section, bullets]) => (
                        <Box key={section}>
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{
                                    mt: 2,
                                    mb: 1,
                                    color: "#4db6ff",
                                    borderLeft: "4px solid #4db6ff",
                                    pl: 1.5
                                }}
                            >
                                {section}
                            </Typography>

                            {Array.isArray(bullets) ? (
                                <Stack component="ul" spacing={1} sx={{ pl: 3 }}>
                                    {bullets.map((b, i) => (
                                        <Typography
                                            component="li"
                                            key={i}
                                            variant="body2"
                                            sx={{ color: "#cfd8dc", lineHeight: 1.6 }}
                                        >
                                            {b}
                                        </Typography>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontStyle: "italic",
                                        mt: 1,
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: "rgba(77,182,255,0.08)",
                                        borderLeft: "4px solid #4db6ff",
                                        color: "#e3f2fd"
                                    }}
                                >
                                    {bullets}
                                </Typography>
                            )}
                        </Box>
                    ))}

                    {/* Read More Button */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            size="small"
                            variant="text"
                            endIcon={<ArrowBack />}
                            onClick={() => setViewMode("compact")}
                            sx={{
                                // backgroundColor: "#4db6ff",
                                color: "white",
                                fontWeight: 500,
                                borderRadius: 2,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "#3aa6f5"
                                }
                            }}
                        >
                            {viewMode === "compact" ? 'Read More' : 'View Less'}
                        </Button>
                    </Box>

                </Stack>
            </Paper>

        );
    };

    return (
        <Box sx={{ py: 10, px: { xs: 2, md: 8 }, bgcolor: "#213750" }} >

            <Box maxWidth='lg' mx='auto'>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 6,
                        flexDirection: { xs: "column", md: "row" },
                        gap: 3
                    }}
                >
                    <Box>
                        <Typography variant='h4' align='left' sx={{ fontWeight: 900, color: "white", fontFamily: `'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif`, }}> Projects</Typography>


                        <Typography variant="subtitle1" sx={{ color: "#90caf9", fontFamily: "italic" }}>
                            Showcasing my key projects in AI, IoT, and Embedded Systems
                        </Typography>
                    </Box>

                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewChange}
                        size="small"
                        sx={{
                            backgroundColor: "#173c5c",
                            borderRadius: 3,
                            p: 0.5
                        }}
                    >
                        <ToggleButton
                            value="compact"
                            sx={{
                                color: "white",
                                border: "none",
                                px: 2,
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.08)"
                                },
                                "&.Mui-selected": {
                                    backgroundColor: "#4db6ff",
                                    color: "#0f2027"
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: "#4db6ff",   // SAME as selected
                                    color: "#0f2027"              // SAME as selected
                                }
                            }}
                        >
                            <ViewModuleIcon sx={{ mr: 1 }} />
                            Compact
                        </ToggleButton>

                        <ToggleButton
                            value="detailed"
                            sx={{
                                color: "white",
                                border: "none",
                                px: 2,
                                "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.08)"
                                },
                                "&.Mui-selected": {
                                    backgroundColor: "#4db6ff",
                                    color: "#0f2027"
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: "#4db6ff",
                                    color: "#0f2027"
                                }
                            }}
                        >
                            <ViewAgendaIcon sx={{ mr: 1 }} />
                            Detailed
                        </ToggleButton>

                    </ToggleButtonGroup>

                </Box>

                {projects.map((proj, idx) => (
                    <ProjectCard
                        key={idx}
                        project={proj}
                        reverse={idx % 2 !== 0}
                        viewMode={viewMode}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default ProjectsSection;
